import dayjs from 'dayjs';
import { useEffect } from 'react';
import { constantFormValues } from '../../root';
import { utcConverter } from '../LeaseUtils/helper';
import InputLabel from 'components/InputLabel/index';
import { Col, DatePicker, Form, Input, Row, Select } from 'antd';

const GeneralLeaseInformation = ({ screen, vendorList = [] }) => {
	const form = Form.useFormInstance();
	const LeaseEndDate = dayjs().add(1, 'month');

	const handleLeaseDateChange = () => {
		const leaseStartDate = form.getFieldValue('leaseStartDate');
		if (!leaseStartDate) return;
		const leaseTerm = form.getFieldValue('leaseTerm');
		if (!leaseTerm) {
			form.setFieldValue(['changes', 0, 'inputChangeEffectiveDate'], utcConverter(leaseStartDate));

			return;
		}

		const leaseEndDate = leaseStartDate.add(leaseTerm, 'month').subtract(1, 'day');

		form.setFieldValue('leaseEndDate', utcConverter(leaseEndDate));
		form.setFieldValue('leaseStartDate', utcConverter(leaseStartDate));
		form.setFieldValue(['changes', 0, 'inputChangeEffectiveDate'], utcConverter(leaseStartDate));
	};

	const currentLeaseTerm = Form.useWatch(constantFormValues?.leaseTerm?.key, form);
	const currentPaymentFrequency = Form.useWatch(constantFormValues?.paymentFrequency?.key, form);

	useEffect(() => {
		const currentPaymentFrequency = form.getFieldValue(constantFormValues?.paymentFrequency?.key);
		if (currentPaymentFrequency === 'Quarterly') {
			form.setFieldValue('modifiedLeaseTerm', Math.floor(form.getFieldValue('leaseTerm') / 3));
		} else {
			form.setFieldValue('modifiedLeaseTerm', form.getFieldValue('leaseTerm'));
		}
	}, [currentLeaseTerm, currentPaymentFrequency]);

	return (
		<Row gutter={[16, 0]} style={{ marginTop: '16px' }}>
			<Form.Item noStyle name="tabIndex" />
			<Form.Item noStyle name="endOfFinancialYear" />
			<Form.Item noStyle name={constantFormValues?.version?.key} />
			<Form.Item name={constantFormValues?.leaseTerm?.key} noStyle />
			<Form.Item noStyle name={constantFormValues?.lessorName?.key} />
			<Form.Item noStyle name={constantFormValues?.paymentModalitylabel?.key} />

			<Col xs={24} md={8}>
				<Form.Item name={constantFormValues?.LeaseId?.key} label={constantFormValues?.LeaseId?.label}>
					<Input disabled placeholder="To be auto generated" style={{ height: '32px', borderRadius: '4px' }} />
				</Form.Item>
			</Col>

			<Col xs={24} md={8}>
				<Form.Item
					name={constantFormValues?.leaseTitle?.key}
					rules={constantFormValues?.leaseTitle?.rules}
					label={constantFormValues?.leaseTitle?.label}
				>
					<Input disabled={screen === 'VIEW'} placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
				</Form.Item>
			</Col>

			<Col xs={24} md={8}>
				<Form.Item
					name={constantFormValues?.lessorID?.key}
					rules={constantFormValues?.lessorID?.rules}
					label={constantFormValues?.lessorID?.label}
				>
					<Select
						showSearch
						filterOption={true}
						optionFilterProp="children"
						placeholder="Select from the list"
						style={{ width: '100%', height: '32px', borderRadius: '4px' }}
						disabled={screen === 'VIEW' || screen === 'EDIT' || screen === 'REVISE'}
						onSelect={(_, selectItem) => form.setFieldValue(constantFormValues?.lessorName?.key, selectItem?.children)}
					>
						{vendorList?.map(({ label, value }) => (
							<Select.Option key={value} value={value}>
								{label}
							</Select.Option>
						))}
					</Select>
				</Form.Item>
			</Col>

			<Col xs={24} md={8}>
				<Form.Item
					name={constantFormValues?.leaseStartDate?.key}
					rules={constantFormValues?.leaseStartDate?.rules}
					label={constantFormValues?.leaseStartDate?.label}
					getValueFromEvent={(e) => {
						const value = e;
						const currentChanges = form.getFieldValue('changes');
						if (screen.toLowerCase() === 'create') {
							form.setFieldValue(['changes', 0, 'inputChangeEffectiveDate'], value);
						} else if (screen.toLowerCase() === 'revise') {
							form.setFieldValue(['changes', currentChanges.length - 1, 'inputChangeEffectiveDate'], value);
						}
						return value;
					}}
				>
					<DatePicker
						disabled={
							screen === 'VIEW' ||
							(screen === 'REVISE'
								? form.getFieldValue('modificationSequence') === 0
									? false
									: true
								: screen === 'CREATE'
									? false
									: true)
						}
						format="YYYY-MM-DD"
						style={{ width: '100%', height: '32px', borderRadius: '4px' }}
						onChange={handleLeaseDateChange}
					/>
				</Form.Item>
			</Col>

			<Col xs={24} md={8}>
				<Form.Item
					name={constantFormValues?.leaseEndDate?.key}
					rules={constantFormValues?.leaseEndDate?.rules}
					label={constantFormValues?.leaseEndDate?.label}
				>
					<DatePicker disabled value={LeaseEndDate} style={{ width: '100%', height: '32px', borderRadius: '4px' }} />
				</Form.Item>
			</Col>

			<Col xs={24} md={8}>
				<Form.Item name="modifiedLeaseTerm" rules={constantFormValues?.leaseTerm?.rules} label={constantFormValues?.leaseTerm?.label}>
					<Input type="number" disabled placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
				</Form.Item>
			</Col>

			<Form.Item noStyle name={constantFormValues?.paymentModality?.key} />

			<Col xs={24} md={8}>
				<Form.Item
					name={constantFormValues?.leaseType?.key}
					rules={constantFormValues?.leaseType?.rules}
					label={constantFormValues?.leaseType?.label}
				>
					<Select
						showSearch
						filterOption={true}
						optionFilterProp="children"
						disabled={screen === 'VIEW'}
						placeholder="Select from the list"
						style={{ width: '100%', height: '32px', borderRadius: '4px' }}
						onSelect={(value) => {
							if (value === 'Operating Lease') {
								form.setFieldValue(['changes', 0, 'inputChangeDiscountRate'], 0);
								form.setFieldValue(constantFormValues?.initialDiscountRate?.key, 0);

								form.setFieldValue('grossRentList', [{ from: 1, to: '', amount: '', remarks: '' }]);
								form.setFieldValue('advanceAmountList', [
									{
										from: 1,
										to: '',
										amount: '',
										totalAmount: '',
									},
								]);
							}
						}}
					>
						{constantFormValues?.leaseType?.options?.map(({ key, value, label }) => (
							<Select.Option key={key} value={value}>
								{label}
							</Select.Option>
						))}
					</Select>
				</Form.Item>
			</Col>

			<Col xs={12}>
				<Form.Item
					shouldUpdate={(previousValue, currentValue) =>
						previousValue?.[constantFormValues?.leaseType?.key] !== currentValue?.[constantFormValues?.leaseType?.key]
					}
					noStyle
				>
					{({ getFieldValue }) => {
						if (getFieldValue(constantFormValues?.leaseType?.key) === 'Finance Lease') {
							return (
								<Row gutter={[16, 0]}>
									<Col xs={24} md={16}>
										<Form.Item
											name={constantFormValues?.initialDiscountRate?.key}
											rules={constantFormValues?.initialDiscountRate?.rules}
											label={constantFormValues?.initialDiscountRate?.label}
											getValueFromEvent={(e) => {
												const value = e.target.value;
												const currentChanges = form.getFieldValue('changes');

												if (screen.toLowerCase() === 'create') {
													form.setFieldValue(['changes', 0, 'inputChangeDiscountRate'], value);
												} else if (screen.toLowerCase() === 'revise') {
													form.setFieldValue(['changes', currentChanges.length - 1, 'inputChangeDiscountRate'], value);
												}
												return value;
											}}
										>
											<Input
												placeholder="Write here"
												disabled={screen === 'VIEW'}
												style={{ height: '32px', borderRadius: '4px' }}
												onBlur={() => {
													if (screen === 'EDIT' && window.debouncedManageChangeRows) {
														window.debouncedManageChangeRows();
													}
												}}
											/>
										</Form.Item>
									</Col>
								</Row>
							);
						} else {
							return <Form.Item name={constantFormValues?.initialDiscountRate?.key} noStyle />;
						}
					}}
				</Form.Item>
			</Col>

			{screen !== 'CREATE' && (
				<>
					<Col xs={24} md={8}>
						<Form.Item name={constantFormValues?.creationDate?.key} label={constantFormValues?.creationDate?.label}>
							<DatePicker
								type="date"
								format="YYYY-MM-DD"
								style={{ width: '100%', height: '32px', borderRadius: '4px' }}
								disabled={screen === 'VIEW' || screen === 'EDIT' || screen === 'REVISE'}
							/>
						</Form.Item>
					</Col>

					<Col xs={24} md={8}>
						<Form.Item name={constantFormValues?.lastModifiedDate?.key} label={constantFormValues?.lastModifiedDate?.label}>
							<DatePicker
								type="date"
								format="YYYY-MM-DD"
								style={{ width: '100%', height: '32px', borderRadius: '4px' }}
								disabled={screen === 'VIEW' || screen === 'EDIT' || screen === 'REVISE'}
							/>
						</Form.Item>
					</Col>
				</>
			)}
		</Row>
	);
};

export default GeneralLeaseInformation;

// import dayjs from 'dayjs';
// import { useSelector } from 'react-redux';
// import { useEffect, useState } from 'react';
// import { constantFormValues } from '../../root';
// import { utcConverter } from '../LeaseUtils/helper';
// import InputLabel from 'components/InputLabel/index';
// import { Col, DatePicker, Form, Input, Row, Select } from 'antd';

// const GeneralLeaseInformation = ({ screen, vendorList = [] }) => {
// 	const form = Form.useFormInstance();
// 	const LeaseEndDate = dayjs().add(1, 'month');

// 	// const [vendorList, setVendorList] = useState([]);

// 	// const companyCode = useSelector((state) => state.profile.profileData.creatorOrganizationId);

// 	// const [getVendorList, { data: vendorListData, isSuccess: isSuccessVendorList }] = useGetQueryV3Mutation();

// 	// --- No changes to the logic ---
// 	const handleLeaseDateChange = () => {
// 		const leaseStartDate = form.getFieldValue('leaseStartDate');
// 		if (!leaseStartDate) return;
// 		const leaseTerm = form.getFieldValue('leaseTerm');
// 		if (!leaseTerm) {
// 			form.setFieldValue(['changes', 0, 'inputChangeEffectiveDate'], utcConverter(leaseStartDate));

// 			return;
// 		}

// 		const leaseEndDate = leaseStartDate.add(leaseTerm, 'month').subtract(1, 'day');

// 		form.setFieldValue('leaseEndDate', utcConverter(leaseEndDate));
// 		form.setFieldValue('leaseStartDate', utcConverter(leaseStartDate));
// 		form.setFieldValue(['changes', 0, 'inputChangeEffectiveDate'], utcConverter(leaseStartDate));
// 	};

// 	const currentLeaseTerm = Form.useWatch(constantFormValues?.leaseTerm?.key, form);
// 	const currentPaymentFrequency = Form.useWatch(constantFormValues?.paymentFrequency?.key, form);

// 	useEffect(() => {
// 		const currentPaymentFrequency = form.getFieldValue(constantFormValues?.paymentFrequency?.key);
// 		if (currentPaymentFrequency === 'Quarterly') {
// 			form.setFieldValue('modifiedLeaseTerm', Math.floor(form.getFieldValue('leaseTerm') / 3));
// 		} else {
// 			form.setFieldValue('modifiedLeaseTerm', form.getFieldValue('leaseTerm'));
// 		}
// 	}, [currentLeaseTerm, currentPaymentFrequency]);
// 	// --- End of logic section ---

// 	// useEffect(() => {
// 	// 	if (companyCode) {
// 	// 		getVendorList({
// 	// 			companyCode,
// 	// 			fieldCodes: [],
// 	// 			fieldValues: [],
// 	// 			name: 'getVendorData',
// 	// 		});
// 	// 	}
// 	// }, [companyCode]);

// 	// useEffect(() => {
// 	// 	if (isSuccessVendorList && vendorListData?.results?.length > 0) {
// 	// 		setVendorList(vendorListData?.results);
// 	// 	}
// 	// }, [isSuccessVendorList, vendorListData]);

// 	return (
// 		<Row gutter={[16, 0]} style={{ marginTop: '16px' }}>
// 			<Form.Item noStyle name={constantFormValues?.version?.key} />
// 			<Form.Item noStyle name={constantFormValues?.paymentModalitylabel?.key} />
// 			<Form.Item noStyle name="tabIndex" />
// 			<Form.Item noStyle name="endOfFinancialYear" />
// 			<Form.Item noStyle name={constantFormValues?.lessorName?.key} />
// 			<Form.Item name={constantFormValues?.leaseTerm?.key} noStyle />

// 			{/* --- Lease ID --- */}
// 			{/* RESPONSIVE CHANGE: Label takes full width on mobile, and original width on medium screens up. */}
// 			<Col xs={24} md={4}>
// 				<InputLabel label={constantFormValues?.LeaseId?.label} head={true} />
// 			</Col>
// 			{/* RESPONSIVE CHANGE: Input takes full width on mobile, and original width on medium screens up. */}
// 			<Col xs={24} md={8}>
// 				<Form.Item name={constantFormValues?.LeaseId?.key}>
// 					<Input disabled placeholder="To be auto generated" style={{ height: '32px', borderRadius: '4px' }} />
// 				</Form.Item>
// 			</Col>

// 			{/* --- Lease Start Date --- */}
// 			<Col xs={24} md={4}>
// 				<InputLabel label={constantFormValues?.leaseStartDate?.label} head={true} />
// 			</Col>
// 			<Col xs={24} md={8}>
// 				<Form.Item
// 					name={constantFormValues?.leaseStartDate?.key}
// 					rules={constantFormValues?.leaseStartDate?.rules}
// 					getValueFromEvent={(e) => {
// 						const value = e;
// 						const currentChanges = form.getFieldValue('changes');
// 						if (screen.toLowerCase() === 'create') {
// 							form.setFieldValue(['changes', 0, 'inputChangeEffectiveDate'], value);
// 						} else if (screen.toLowerCase() === 'revise') {
// 							form.setFieldValue(['changes', currentChanges.length - 1, 'inputChangeEffectiveDate'], value);
// 						}
// 						return value;
// 					}}
// 				>
// 					<DatePicker
// 						disabled={
// 							screen === 'VIEW' ||
// 							(screen === 'REVISE'
// 								? form.getFieldValue('modificationSequence') === 0
// 									? false
// 									: true
// 								: screen === 'CREATE'
// 									? false
// 									: true)
// 						}
// 						format="YYYY-MM-DD"
// 						style={{ width: '100%', height: '32px', borderRadius: '4px' }}
// 						onChange={handleLeaseDateChange}
// 					/>
// 				</Form.Item>
// 			</Col>

// 			{/* --- Lease Title --- */}
// 			<Col xs={24} md={4}>
// 				<InputLabel label={constantFormValues?.leaseTitle?.label} head={true} />
// 			</Col>
// 			<Col xs={24} md={8}>
// 				<Form.Item name={constantFormValues?.leaseTitle?.key} rules={constantFormValues?.leaseTitle?.rules}>
// 					<Input disabled={screen === 'VIEW'} placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
// 				</Form.Item>
// 			</Col>

// 			{/* --- Lessor Name --- */}
// 			<Col xs={24} md={4}>
// 				<InputLabel label={constantFormValues?.lessorID?.label} head={true} />
// 			</Col>
// 			<Col xs={24} md={8}>
// 				<Form.Item name={constantFormValues?.lessorID?.key} rules={constantFormValues?.lessorID?.rules}>
// 					<Select
// 						showSearch
// 						filterOption={true}
// 						optionFilterProp="children"
// 						placeholder="Select from the list"
// 						style={{ width: '100%', height: '32px', borderRadius: '4px' }}
// 						disabled={screen === 'VIEW' || screen === 'EDIT' || screen === 'REVISE'}
// 						onSelect={(_, selectItem) => form.setFieldValue(constantFormValues?.lessorName?.key, selectItem?.children)}
// 					>
// 						{vendorList?.map(({ label, value }) => (
// 							<Select.Option key={value} value={value}>
// 								{label}
// 							</Select.Option>
// 						))}
// 					</Select>
// 				</Form.Item>
// 			</Col>

// 			{/* --- Lessor Name --- */}
// 			{/* <Col xs={24} md={4}>
// 				<InputLabel label={constantFormValues?.lessorName?.label} head={true} />
// 			</Col>
// 			<Col xs={24} md={8}>
// 				<Form.Item name={constantFormValues?.lessorName?.key} rules={constantFormValues?.lessorName?.rules}>
// 					<Input disabled={screen === 'VIEW'} placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
// 				</Form.Item>
// 			</Col> */}

// 			{/* --- Lease Term --- */}
// 			<Col xs={24} md={4}>
// 				<InputLabel label={constantFormValues?.leaseTerm?.label} head={true} />
// 			</Col>
// 			<Col xs={24} md={8}>
// 				<Form.Item name="modifiedLeaseTerm" rules={constantFormValues?.leaseTerm?.rules} noStyle>
// 					<Input type="number" disabled placeholder="Write here" style={{ height: '32px', borderRadius: '4px' }} />
// 				</Form.Item>
// 			</Col>

// 			{/* --- Lease End Date --- */}
// 			<Col xs={24} md={4}>
// 				<InputLabel label={constantFormValues?.leaseEndDate?.label} head={true} />
// 			</Col>
// 			<Col xs={24} md={8}>
// 				<Form.Item name={constantFormValues?.leaseEndDate?.key} rules={constantFormValues?.leaseEndDate?.rules}>
// 					<DatePicker disabled value={LeaseEndDate} style={{ width: '100%', height: '32px', borderRadius: '4px' }} />
// 				</Form.Item>
// 			</Col>

// 			<Form.Item noStyle name={constantFormValues?.paymentModality?.key} />

// 			{/* --- Lease Type --- */}
// 			<Col xs={24} md={4}>
// 				<InputLabel label={constantFormValues?.leaseType?.label} head={true} />
// 			</Col>
// 			<Col xs={24} md={8}>
// 				<Form.Item name={constantFormValues?.leaseType?.key} rules={constantFormValues?.leaseType?.rules}>
// 					<Select
// 						disabled={screen === 'VIEW'}
// 						showSearch
// 						style={{ width: '100%', height: '32px', borderRadius: '4px' }}
// 						filterOption={true}
// 						placeholder="Select from the list"
// 						optionFilterProp="children"
// 						onSelect={(value) => {
// 							if (value === 'Operating Lease') {
// 								form.setFieldValue(['changes', 0, 'inputChangeDiscountRate'], 0);
// 								form.setFieldValue(constantFormValues?.initialDiscountRate?.key, 0);

// 								form.setFieldValue('grossRentList', [{ from: 1, to: '', amount: '', remarks: '' }]);
// 								form.setFieldValue('advanceAmountList', [
// 									{
// 										from: 1,
// 										to: '',
// 										amount: '',
// 										totalAmount: '',
// 									},
// 								]);
// 							}
// 						}}
// 					>
// 						{constantFormValues?.leaseType?.options?.map(({ key, value, label }) => (
// 							<Select.Option key={key} value={value}>
// 								{label}
// 							</Select.Option>
// 						))}
// 					</Select>
// 				</Form.Item>
// 			</Col>

// 			{/* --- Initial Discount Rate --- */}
// 			<Col xs={12}>
// 				<Form.Item
// 					shouldUpdate={(previousValue, currentValue) =>
// 						previousValue?.[constantFormValues?.leaseType?.key] !== currentValue?.[constantFormValues?.leaseType?.key]
// 					}
// 					noStyle
// 				>
// 					{({ getFieldValue }) => {
// 						if (getFieldValue(constantFormValues?.leaseType?.key) === 'Finance Lease') {
// 							return (
// 								<Row gutter={[16, 0]}>
// 									<Col xs={24} md={8}>
// 										<InputLabel label={constantFormValues?.initialDiscountRate?.label} head={true} />
// 									</Col>
// 									<Col xs={24} md={16}>
// 										<Form.Item
// 											name={constantFormValues?.initialDiscountRate?.key}
// 											rules={constantFormValues?.initialDiscountRate?.rules}
// 											getValueFromEvent={(e) => {
// 												const value = e.target.value;
// 												const currentChanges = form.getFieldValue('changes');
// 												if (screen.toLowerCase() === 'create') {
// 													form.setFieldValue(['changes', 0, 'inputChangeDiscountRate'], value);
// 												} else if (screen.toLowerCase() === 'revise') {
// 													form.setFieldValue(['changes', currentChanges.length - 1, 'inputChangeDiscountRate'], value);
// 												}
// 												return value;
// 											}}
// 										>
// 											<Input
// 												disabled={screen === 'VIEW'}
// 												placeholder="Write here"
// 												style={{ height: '32px', borderRadius: '4px' }}
// 												onBlur={() => {
// 													if (screen === 'EDIT' && window.debouncedManageChangeRows) {
// 														window.debouncedManageChangeRows();
// 													}
// 												}}
// 											/>
// 										</Form.Item>
// 									</Col>
// 								</Row>
// 							);
// 						} else {
// 							return <Form.Item name={constantFormValues?.initialDiscountRate?.key} noStyle />;
// 						}
// 					}}
// 				</Form.Item>
// 			</Col>

// 			{/* --- Creation & Modified Dates (Conditional) --- */}
// 			{screen !== 'CREATE' && (
// 				<>
// 					<Col xs={24} md={4}>
// 						<InputLabel label={constantFormValues?.creationDate?.label} head={true} />
// 					</Col>
// 					<Col xs={24} md={8}>
// 						<Form.Item name={constantFormValues?.creationDate?.key}>
// 							<DatePicker
// 								type="date"
// 								disabled={screen === 'VIEW' || screen === 'EDIT' || screen === 'REVISE'}
// 								format="YYYY-MM-DD"
// 								style={{ width: '100%', height: '32px', borderRadius: '4px' }}
// 							/>
// 						</Form.Item>
// 					</Col>
// 					<Col xs={24} md={4}>
// 						<InputLabel label={constantFormValues?.lastModifiedDate?.label} head={true} />
// 					</Col>
// 					<Col xs={24} md={8}>
// 						<Form.Item name={constantFormValues?.lastModifiedDate?.key}>
// 							<DatePicker
// 								type="date"
// 								disabled={screen === 'VIEW' || screen === 'EDIT' || screen === 'REVISE'}
// 								format="YYYY-MM-DD"
// 								style={{ width: '100%', height: '32px', borderRadius: '4px' }}
// 							/>
// 						</Form.Item>
// 					</Col>
// 				</>
// 			)}
// 		</Row>
// 	);
// };

// export default GeneralLeaseInformation;
